import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserSearchIcon, SendIcon, CheckIcon, BuildingIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
const tabs = [{
  id: 'search',
  icon: SearchIcon,
  title: 'Search local businesses',
  description: 'Filter by industry, location, revenue, and employee count to find the exact local businesses you want to target. Our database covers 200M+ businesses across every category.'
}, {
  id: 'find',
  icon: UserSearchIcon,
  title: 'Find business owner contacts',
  description: 'Instantly access verified business owner emails, direct phone numbers, titles, and LinkedIn profiles for every local business in your search results.'
}, {
  id: 'outreach',
  icon: SendIcon,
  title: 'Launch outreach to owners',
  description: 'Build multi-step email sequences and reach local business owners directly from Openmart. No need to export to a third-party tool.'
}];
export function OwnerFinderWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-4018">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4019">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4020">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4021">
            How the local business{' '}
            <span className="text-blue-600" data-id="element-4022">owner finder</span> works
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4023">
            From search to outreach in three simple steps.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-4024">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-4025">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-4026">
                  <div className="flex items-start gap-6" data-id="element-4027">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-4028">
                      <tab.icon className="w-6 h-6" data-id="element-4029" />
                    </div>
                    <div data-id="element-4030">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-4031">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-4032">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-4033">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-4034">
              <AnimatePresence mode="wait" data-id="element-4035">
                {activeTab === 'search' && <motion.div key="search" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-4036">
                    <h4 className="font-bold text-slate-900 mb-6 text-center" data-id="element-4037">
                      Search criteria
                    </h4>
                    <div className="space-y-4 mb-6" data-id="element-4038">
                      <div data-id="element-4039">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-4040">
                          Industry
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-slate-100 flex items-center gap-2" data-id="element-4041">
                          <BuildingIcon className="w-4 h-4 text-slate-400" data-id="element-4042" />{' '}
                          Restaurants
                        </div>
                      </div>
                      <div data-id="element-4043">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-4044">
                          Location
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium text-slate-700 border border-slate-100 flex items-center gap-2" data-id="element-4045">
                          <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-4046" /> San
                          Francisco, CA
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 text-center" data-id="element-4047">
                      <p className="text-sm font-semibold text-blue-900" data-id="element-4048">
                        4,250 local businesses found
                      </p>
                      <p className="text-xs text-blue-700 mt-1" data-id="element-4049">
                        Ready to find business owners
                      </p>
                    </div>
                  </motion.div>}

                {activeTab === 'find' && <motion.div key="find" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md space-y-4" data-id="element-4050">
                    {[{
                  name: 'Sarah Johnson',
                  title: 'Owner',
                  email: 'sarah@grandcentralbakery.com',
                  phone: '(415) 555-0198'
                }, {
                  name: 'Mark Rivera',
                  title: 'Founder',
                  email: 'mark@localpetsupplies.com',
                  phone: '(415) 555-0234'
                }].map((owner, idx) => <div key={idx} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-id="element-4051">
                        <div className="flex items-start justify-between mb-4" data-id="element-4052">
                          <div className="flex items-center gap-3" data-id="element-4053">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold" data-id="element-4054">
                              {owner.name.charAt(0)}
                            </div>
                            <div data-id="element-4055">
                              <h4 className="text-sm font-bold text-slate-900" data-id="element-4056">
                                {owner.name}
                              </h4>
                              <p className="text-xs text-slate-500" data-id="element-4057">
                                {owner.title}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2" data-id="element-4058">
                          <div className="flex items-center gap-2 text-sm font-medium text-blue-600 bg-blue-50 px-3 py-2 rounded-lg" data-id="element-4059">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4060" />
                            <MailIcon className="w-4 h-4" data-id="element-4061" /> {owner.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-slate-50 px-3 py-2 rounded-lg" data-id="element-4062">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4063" />
                            <PhoneIcon className="w-4 h-4 text-slate-400" data-id="element-4064" />{' '}
                            {owner.phone}
                          </div>
                        </div>
                      </div>)}
                  </motion.div>}

                {activeTab === 'outreach' && <motion.div key="outreach" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-4065">
                    <div className="flex items-center gap-3 mb-6" data-id="element-4066">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-4067">
                        <SendIcon className="w-5 h-5 text-blue-600" data-id="element-4068" />
                      </div>
                      <div data-id="element-4069">
                        <h4 className="font-bold text-slate-900" data-id="element-4070">
                          Owner outreach campaign
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-4071">
                          Sending to 4,250 verified emails
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-4072">
                      <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-4073">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-blue-500 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-4074">
                          <MailIcon className="w-4 h-4" data-id="element-4075" />
                        </div>
                        <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-4076">
                          <div className="flex items-center justify-between mb-1" data-id="element-4077">
                            <h5 className="font-bold text-slate-900 text-sm" data-id="element-4078">
                              Step 1: Introduction
                            </h5>
                          </div>
                          <p className="text-xs text-slate-500" data-id="element-4079">
                            Sent immediately
                          </p>
                        </div>
                      </div>

                      <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-4080">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-200 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-4081">
                          <MailIcon className="w-4 h-4" data-id="element-4082" />
                        </div>
                        <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-slate-100" data-id="element-4083">
                          <div className="flex items-center justify-between mb-1" data-id="element-4084">
                            <h5 className="font-bold text-slate-900 text-sm" data-id="element-4085">
                              Step 2: Follow up
                            </h5>
                          </div>
                          <p className="text-xs text-slate-500" data-id="element-4086">Wait 3 days</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}